import axios from "axios";
import { refreshTokenFunction } from "../../context/AuthContext";
import { toErrorWithMessage } from "../../utils/error";
import { pageRequestCache } from "../../utils/requestCache";

// Remove trailing slash to prevent double-slash issues
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "https://clinica-server.replit.app").replace(/\/+$/, "");

const endpointAliases = {
  institutional_table_data: "institutional_table_data",
  table_data: "institutional_table_data",
  institutional_treemap_data: "institutional_treemap_data",
  treemap_data: "institutional_treemap_data",
  treemap: "institutional_treemap_data",
};

const endpointConfig = {
  institutional_table_data: {
    defaultParams: {
      page_size: 500,
      page: 1,
    },
    systemsPath: "cleaned_idn_list",
    dataPath: "data",
  },
  institutional_treemap_data: {
    allowsParams: false,
    dataPath: "data",
    timeout: 90000,
  },
};

const splitEndpoint = (endPoint = "") => {
  if (!endPoint) {
    return { path: "", query: "" };
  }

  const [path, ...queryParts] = endPoint.split("?");
  return {
    path: path.trim(),
    query: queryParts.join("?").trim(),
  };
};

const normalizeEndpoint = (endPoint = "") => {
  const { path } = splitEndpoint(endPoint);
  const canonical = path.toLowerCase().trim();
  return endpointAliases[canonical] || path;
};

const buildAxiosConfig = ({ token, params, rawResponse, timeout }) => {
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    timeout: typeof timeout === "number" ? timeout : 120000,
  };

  if (params && Object.keys(params).length > 0) {
    config.params = params;
  }

  if (rawResponse) {
    config.transformResponse = [(data) => data];
  }

  return config;
};

const parseResponse = (response, { rawResponse, config }) => {
  if (rawResponse) {
    return response.data;
  }

  const { data } = response;
  if (!data) {
    return data;
  }

  const dataPath = config?.dataPath;
  const systemsPath = config?.systemsPath;

  if (dataPath && data[dataPath]) {
    return {
      data: data[dataPath],
      systems: systemsPath ? data[systemsPath] || [] : undefined,
      pagination: data.total
        ? {
            total: data.total,
            page: data.page || 1,
            pageSize: data.page_size || data[dataPath]?.length || 0,
            totalPages: data.page_size
              ? Math.ceil(data.total / data.page_size)
              : 1,
          }
        : undefined,
    };
  }

  return data;
};

const buildError = (error, extra = {}) => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const message =
      error.response?.data?.message || error.message || "API request failed";

    const enhancedError = new Error(message);
    enhancedError.status = status;
    enhancedError.data = error.response?.data;
    return Object.assign(enhancedError, extra);
  }

  const normalizedError = toErrorWithMessage(
    error,
    "API request failed. Please try again."
  );

  return Object.assign(normalizedError, extra);
};

const appendQuery = (path, query) => {
  if (!query) {
    return path;
  }

  const sanitizedQuery = query.replace(/^[?&]+/, "");
  return sanitizedQuery.length > 0 ? `${path}?${sanitizedQuery}` : path;
};

const getEndpointConfig = (endpoint) => {
  const normalized = normalizeEndpoint(endpoint);
  return {
    normalized,
    config: endpointConfig[normalized] || {},
  };
};

const resolveParams = ({ config, params, hasInlineQuery }) => {
  if (config.allowsParams === false) {
    return undefined;
  }

  if (hasInlineQuery) {
    return params;
  }

  return {
    ...(config.defaultParams || {}),
    ...(params || {}),
  };
};

const axiosClient = axios.create({ baseURL: API_BASE_URL });

const fetchDataStats = async (
  endPoint,
  token,
  refreshToken,
  params = {},
  rawResponse = false,
) => {
  if (!token || !refreshToken) {
    throw new Error("Authentication tokens are missing");
  }

  const { path, query } = splitEndpoint(endPoint);
  const { normalized, config } = getEndpointConfig(path);

  const requestPath = appendQuery(`/${normalized}`, query);
  const resolvedParams = resolveParams({
    config,
    params,
    hasInlineQuery: Boolean(query),
  });

  const makeRequest = async (authToken) =>
    axiosClient.get(requestPath, {
      ...buildAxiosConfig({
        token: authToken,
        params: resolvedParams,
        rawResponse,
        timeout: config?.timeout,
      }),
    });

  try {
    const response = await makeRequest(token);
    return parseResponse(response, { rawResponse, config });
  } catch (error) {
    const status = error?.response?.status;

    if (status === 401 && refreshToken) {
      const refreshedToken = await refreshTokenFunction(refreshToken);

      if (refreshedToken) {
        const retryResponse = await makeRequest(refreshedToken);
        return parseResponse(retryResponse, { rawResponse, config });
      }

      const sessionExpiredError = new Error(
        "Your session has expired. Please sign in again."
      );
      sessionExpiredError.status = 401;
      throw Object.assign(sessionExpiredError, { endpoint: normalized });
    }

    if (
      status === 500 &&
      config.allowsParams !== false &&
      normalized === "institutional_treemap_data" &&
      resolvedParams
    ) {
      try {
        const response = await axiosClient.get(`/${normalized}`, {
          ...buildAxiosConfig({ token, rawResponse, timeout: config?.timeout }),
        });

        return parseResponse(response, { rawResponse, config });
      } catch (retryError) {
        throw buildError(retryError, { endpoint: normalized });
      }
    }

    throw buildError(error, { endpoint: normalized });
  }
};

// Only analytical reads: preferences, accounts, saved lists and auth always go live.
const cacheableData = /^(data_stats_\d+|sankey_data_\d+|national_data(?:_quarterly)?|state_level_(?:data|quarterly)|region_level_(?:data|quarterly)|hcp_data|institutional_table_data|plan_table_data)$/;

export const getDataStats = (endPoint, token, refreshToken, params = {}, rawResponse = false) => {
  const load = () => fetchDataStats(endPoint, token, refreshToken, params, rawResponse);
  const pageSize = Number(params.page_size || new URLSearchParams(splitEndpoint(endPoint).query).get("page_size") || 10);
  if (!token || !refreshToken || pageSize > 500 || !cacheableData.test(normalizeEndpoint(endPoint))) {
    return load();
  }
  const key = JSON.stringify([token, endPoint,
    Object.keys(params).sort().map((key) => [key, params[key]]), rawResponse]);
  return pageRequestCache.get(key, load);
};

export default getDataStats;
